import React, { useState, useContext, useEffect } from "react";
import "./FreelancerWorkSpace.css";
import { Briefcase, FileText, Globe, AlertTriangle, Edit3 } from "lucide-react";
import PromptBar from "./PromptBar";
import ChatHistoryItem from "../modules/Historical List/ChatHistoryItem";
import RapidMindAgentButton from "./RapidMindAgentButton";
import { WorkspaceProvider, useWorkspace } from "../context/WorkspaceContext.js";

const FreelancerWorkspace = ({ user }) => {
  const { workspaceData, updateWorkspaceData } = useWorkspace();
  const [risks, setRisks] = useState([
    "Late client payments",
    "Project scope creep",
    "Burnout risk",
  ]);

  const [newRisk, setNewRisk] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [aiResponse, setAiResponse] = useState('');
  const [chatMessages, setChatMessages] = useState([]);

  // New states for professional expertise selector
  const [selectedExpertise, setSelectedExpertise] = useState([]);
  const [quickQuestion, setQuickQuestion] = useState("");
  const [recommendedTools, setRecommendedTools] = useState([]);
  const [isRecommending, setIsRecommending] = useState(false);

  // Sync with workspace context
  useEffect(() => {
    updateWorkspaceData({
      userType: 'freelancer',
      selectedExpertise: selectedExpertise,
      currentTask: quickQuestion,
      recommendedTools: recommendedTools
    });
  }, [selectedExpertise, quickQuestion, recommendedTools, updateWorkspaceData]);

  // Automatically recommend tools when task is entered
  useEffect(() => {
    if (selectedExpertise.length > 0 && quickQuestion.trim() && !isRecommending && recommendedTools.length === 0) {
      getToolRecommendations();
    }
  }, [selectedExpertise, quickQuestion, isRecommending, recommendedTools.length]);



  const expertiseOptions = [
    "Creative & Design",
    "Writing & Translation",
    "Digital Marketing & Sales",
    "Web & Software Development",
    "Admin & Business Support",
    "Finance & Legal"
  ];

  const toolRecommendations = {
    "Creative & Design": ["Adobe Creative Suite", "Figma", "Canva", "Sketch"],
    "Writing & Translation": ["Grammarly", "Hemingway", "Google Translate", "Scrivener"],
    "Digital Marketing & Sales": ["Google Analytics", "HubSpot", "Mailchimp", "SEMrush"],
    "Web & Software Development": ["VS Code", "GitHub", "Docker", "Postman"],
    "Admin & Business Support": ["Microsoft Office 365", "Trello", "Slack", "Zoom"],
    "Finance & Legal": ["QuickBooks", "Stripe", "DocuSign", "LegalZoom"]
  };

  const addRisk = () => {
    if (newRisk.trim() !== "") {
      setRisks([...risks, newRisk]);
      setNewRisk("");
    }
  };

  const handleExpertiseChange = (expertise) => {
    setSelectedExpertise(prev =>
      prev.includes(expertise)
        ? prev.filter(e => e !== expertise)
        : [...prev, expertise]
    );
  };

  const getToolRecommendations = () => {
    if (selectedExpertise.length === 0) return;

    setIsRecommending(true);
    const tools = selectedExpertise.flatMap(exp => toolRecommendations[exp] || []);
    setRecommendedTools([...new Set(tools)]); // Remove duplicates
    setIsRecommending(false);
  };



  const handleSearch = async (query, results = null) => {
    if (!query.trim()) return;

    setIsSearching(true);
    try {
      // If results are provided from PromptBar, use them
      if (results && results.results && results.results.length > 0) {
        console.log('Using web search results from PromptBar:', results);
        setSearchResults(results.results);
        setIsSearching(false);
        return;
      }

      // Fallback: perform search directly if no results provided
      const response = await fetch('/api/search/files', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          query: query.trim(),
          maxResults: 10,
          caseSensitive: false
        }),
        credentials: 'include',
      });

      if (response.ok) {
        const searchResults = await response.json();
        setSearchResults(searchResults.results || []);
      } else {
        console.error('File search failed');
        setSearchResults([]);
      }
    } catch (error) {
      console.error('Search error:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  return (
    <div id="FreelancerWorkSpace" className="workspace-container">
      <header className="workspace-header">
        <h1>🚀 Freelancer Workspace</h1>
        <p>Welcome back, {user?.name || "Freelancer"} 👋</p>
      </header>

      {/* Prompt Bar */}
      <div style={{ padding: '16px', background: '#f8f9fa' }}>
        <PromptBar
          onSearch={handleSearch}
          onAIResponse={(response) => {
            setAiResponse(response);
            setChatMessages(prev => [...prev, { type: 'ai', content: response, timestamp: new Date() }]);
          }}
          onSearchResults={(results) => {
            setSearchResults(results);
            setChatMessages(prev => [...prev, { type: 'search', content: results, timestamp: new Date() }]);
          }}
        />
        
      </div>

      {/* Chat Area */}
      <div className="chat-container">
        {chatMessages.map((message, idx) => (
          <div key={idx} className={`chat-message ${message.type}`}>
            {message.type === 'ai' && (
              <div className="ai-message">
                <div className="ai-avatar">🤖</div>
                <div className="ai-content">
                  <pre>{message.content}</pre>
                </div>
              </div>
            )}
            {message.type === 'search' && (
              <div className="search-message">
                <div className="search-avatar">🔍</div>
                <div className="search-content">
                  <h4>Search Results:</h4>
                  {message.content.map((result, resultIdx) => (
                    <div key={resultIdx} className="search-result-item">
                      <h5>
                        <a href={result.url} target="_blank" rel="noopener noreferrer">
                          {result.title}
                        </a>
                      </h5>
                      <p>{result.description}</p>
                      <small>{result.url}</small>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* PromptBar Layout with Historical Research and Summarize */}
      <div className="promptbar-layout">
        {/* Historical Research Section */}
        <div className="historical-research-section">
          <h4>📚 Historical Research</h4>
          <div className="historical-list">
            <ChatHistoryItem
              title="Freelancer CV Generation"
              date="2 hours ago"
              onClick={() => console.log('Load CV generation history')}
            />
            <ChatHistoryItem
              title="Portfolio Design Ideas"
              date="1 day ago"
              onClick={() => console.log('Load portfolio history')}
            />
            <ChatHistoryItem
              title="Proposal Writing Tips"
              date="3 days ago"
              onClick={() => console.log('Load proposal history')}
            />
            <ChatHistoryItem
              title="Client Communication"
              date="1 week ago"
              onClick={() => console.log('Load communication history')}
            />
          </div>
        </div>

        {/* Quick Summarize Section */}
        <div className="quick-summarize-section">
          <h4>⚡ Quick Summarize</h4>
          <div className="summarize-input-container">
            <textarea
              className="summarize-textarea"
              placeholder="Paste text here to summarize..."
              rows="6"
            />
            <button className="summarize-button">Summarize</button>
          </div>
          <div className="summarize-response">
            <pre>Summary will appear here...</pre>
          </div>
        </div>
      </div>

      {/* Professional Expertise Selector */}
      <section className="workspace-section">
        <h2>🎯 Select your professional expertise (Your Skill Specialization)</h2>
        <div className="expertise-grid">
          {expertiseOptions.map((option, idx) => (
            <label
              key={idx}
              className={`expertise-option ${selectedExpertise.includes(option) ? 'selected' : ''}`}
              onClick={() => handleExpertiseChange(option)}
            >
              <input
                type="checkbox"
                checked={selectedExpertise.includes(option)}
                onChange={() => handleExpertiseChange(option)}
                style={{ display: 'none' }} // Hide the checkbox, use label for styling
              />
              {idx + 1}. {option}
            </label>
          ))}
        </div>
        {selectedExpertise.length > 0 && (
          <div className="quick-question-section">
            <h3>What specific task are you working on?</h3>
            <input
              type="text"
              placeholder="e.g., Logo design for a startup, SEO optimization..."
              value={quickQuestion}
              onChange={(e) => setQuickQuestion(e.target.value)}
            />
            <button
              onClick={getToolRecommendations}
              disabled={isRecommending}
              className="recommend-button"
            >
              {isRecommending ? "Recommending..." : "Get High-Performance Tool Recommendations"}
            </button>
          </div>
        )}
        {recommendedTools.length > 0 && (
          <div className="recommended-tools">
            <h3>🛠️ Recommended Tools:</h3>
            <ul>
              {recommendedTools.map((tool, idx) => (
                <li key={idx}>{tool}</li>
              ))}
            </ul>
            <div className="auto-agent-notice">
              <p>🤖 RapidMind AI Agent is now automatically working on your task...</p>
            </div>
            <RapidMindAgentButton />
          </div>
        )}
      </section>



      {/* AI Professional Services */}
      <section className="workspace-section">
        <h2><Briefcase size={20}/> Professional Services</h2>
        <div className="services-grid">
          <button className="service-card">
            <FileText size={24}/> Generate CV with AI
          </button>
          <button className="service-card">
            <Globe size={24}/> Build Portfolio
          </button>
          <button className="service-card">
            <Edit3 size={24}/> Write Smart Proposals
          </button>
        </div>
      </section>

      {/* Platform Integrations */}
      <section className="workspace-section">
        <h2>🌍 High-Paid Platforms</h2>
        <div className="platform-grid">
          <a href="https://www.upwork.com" target="_blank" rel="noreferrer">Upwork</a>
          <a href="https://www.fiverr.com" target="_blank" rel="noreferrer">Fiverr</a>
          <a href="https://www.toptal.com" target="_blank" rel="noreferrer">Toptal</a>
          <a href="https://www.freelancer.com" target="_blank" rel="noreferrer">Freelancer</a>
        </div>
      </section>

      {/* Search Results Display */}
      {searchResults.length > 0 && (
        <section className="workspace-section">
          <h2>🔍 Search Results</h2>
          <div className="search-results">
            {searchResults.map((result, idx) => (
              <div key={idx} className="search-result-item">
                <h4>
                  <a href={result.url} target="_blank" rel="noopener noreferrer">
                    {result.title}
                  </a>
                </h4>
                <p>{result.description}</p>
                <small>{result.url}</small>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Risk Management */}
      <section className="workspace-section">
        <h2><AlertTriangle size={20}/> Risk Management</h2>
        <ul className="risk-list">
          {risks.map((risk, idx) => (
            <li key={idx}>{risk}</li>
          ))}
        </ul>
        <div className="risk-input">
          <input
            type="text"
            placeholder="Add new risk..."
            value={newRisk}
            onChange={(e) => setNewRisk(e.target.value)}
          />
          <button onClick={addRisk}>Add</button>
        </div>
      </section>
    </div>
  );
};

export default FreelancerWorkspace;
